import { useEffect, useRef, useState, useCallback } from "react";

/**
 * Handles animated value replacement by closing the current value,
 * waiting for a transition to finish, then swapping in the next value.
 *
 * Ideal for UI elements that must animate out before content changes.
 */
export function useDelayedSwap({ value, delay, isInitiallyVisible = true }) {
  // Manages: when `value` changes, go "closing", then swap after `delay`.
  const [displayed, setDisplayed] = useState(value);
  const [isClosing, setIsClosing] = useState(false);
  const timeoutRef = useRef(null);

  useEffect(() => {
    const changed = value !== displayed;
    if (!changed) return;

    if (displayed != null || isInitiallyVisible === false) {
      setIsClosing(true);
      clearTimeout(timeoutRef.current);

      timeoutRef.current = setTimeout(() => {
        setDisplayed(value);
        setIsClosing(false);
      }, delay);
    } else {
      // first show
      setDisplayed(value);
      setIsClosing(false);
    }

    return () => clearTimeout(timeoutRef.current);
  }, [value, displayed, delay, isInitiallyVisible]);

  const close = useCallback(() => {
    setIsClosing(true);
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setDisplayed(null);
      setIsClosing(false);
    }, delay);
  }, [delay]);

  return { displayed, isClosing, close, setDisplayed };
}